import React, { useCallback, useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Plus, Trash2, Pencil, ImagePlus, Building2, Globe } from "lucide-react";

const KINDS = [
  { value: "lending", label: "Lending Partners" },
  { value: "insurance", label: "Insurance Partners" },
  { value: "investor", label: "Investors" },
];

function compressImage(file, maxW = 400, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const empty = (kind) => ({
  kind, name: "", entity_type: "", registration: "", products: "", role: "",
  grievance: "", website: "", logo: "", active: true, order: 0,
});

function PartnerForm({ initial, kind, onSave }) {
  const [form, setForm] = useState(initial || empty(kind));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const logo = await compressImage(file); setForm((f) => ({ ...f, logo })); }
    catch { toast.error("Could not process image."); }
  };
  const isInvestor = form.kind === "investor";
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border bg-white">
          {form.logo ? <img src={form.logo} alt="logo" className="h-full w-full object-contain" /> : <Building2 className="h-6 w-6 text-muted-foreground" />}
        </div>
        <div>
          <input id="partner-logo" type="file" accept="image/*" className="hidden" onChange={onFile} />
          <Button type="button" variant="outline" onClick={() => document.getElementById("partner-logo").click()}>
            <ImagePlus className="mr-1.5 h-4 w-4" />Upload Logo
          </Button>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2"><Label>Legal Name</Label>
          <Input value={form.name} onChange={set("name")} placeholder="Full legal name" /></div>
        <div className="space-y-1.5"><Label>Type</Label>
          <Input value={form.entity_type} onChange={set("entity_type")} placeholder={isInvestor ? "VC / Angel / Institutional" : "Bank / NBFC / Insurer"} /></div>
        {!isInvestor && (
          <div className="space-y-1.5"><Label>Registration (if applicable)</Label>
            <Input value={form.registration} onChange={set("registration")} placeholder="RBI / IRDAI reg no." /></div>
        )}
        <div className="space-y-1.5 sm:col-span-2"><Label>{isInvestor ? "About" : "Products"}</Label>
          <Textarea rows={2} value={form.products} onChange={set("products")} placeholder={isInvestor ? "Short description" : "Products offered"} /></div>
        {!isInvestor && (
          <>
            <div className="space-y-1.5"><Label>Our Role</Label>
              <Input value={form.role} onChange={set("role")} placeholder="LSP / Referral / DSA" /></div>
            <div className="space-y-1.5"><Label>Grievance Escalation</Label>
              <Input value={form.grievance} onChange={set("grievance")} placeholder="Escalation route" /></div>
          </>
        )}
        <div className="space-y-1.5"><Label>Website</Label>
          <Input value={form.website} onChange={set("website")} placeholder="https://…" /></div>
        <div className="space-y-1.5"><Label>Display Order</Label>
          <Input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) || 0 }))} /></div>
        <div className="flex items-center gap-3 pt-6">
          <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
          <Label className="cursor-pointer">Active (show on site)</Label>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={() => { if (!form.name) return toast.error("Enter a name."); onSave(form); }}>Save</Button>
      </DialogFooter>
    </div>
  );
}

export default function AdminPartners() {
  const [kind, setKind] = useState("lending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/partners?kind=${kind}`); setItems(data); }
    catch { toast.error("Failed to load partners"); }
    finally { setLoading(false); }
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const create = async (form) => {
    try { await api.post("/admin/partners", { ...form, kind }); toast.success("Added."); setAddOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };
  const update = async (form) => {
    try { await api.patch(`/admin/partners/${edit.id}`, form); toast.success("Updated."); setEdit(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const toggle = async (p) => { try { await api.patch(`/admin/partners/${p.id}`, { active: !p.active }); load(); } catch { toast.error("Failed"); } };
  const remove = async (p) => { try { await api.delete(`/admin/partners/${p.id}`); toast.success("Deleted."); load(); } catch { toast.error("Failed"); } };

  const label = KINDS.find((k) => k.value === kind)?.label;

  return (
    <Card className="border">
      <div className="flex flex-col gap-4 border-b p-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold">Partners & Investors</h2>
          <p className="text-sm text-muted-foreground">Add, edit or remove lending partners, insurance partners and investors.</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild><Button><Plus className="mr-1.5 h-4 w-4" />Add {label?.replace(" Partners", "").replace("Investors", "Investor")}</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
            <DialogHeader><DialogTitle>Add to {label}</DialogTitle>
              <DialogDescription>Only add verified entities with accurate details.</DialogDescription></DialogHeader>
            <PartnerForm kind={kind} onSave={create} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="border-b p-4">
        <Tabs value={kind} onValueChange={setKind}>
          <TabsList>
            {KINDS.map((k) => <TabsTrigger key={k.value} value={k.value}>{k.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>
      </div>

      <div className="p-4">
        {loading ? <p className="py-12 text-center text-muted-foreground">Loading…</p>
          : items.length === 0 ? <p className="py-12 text-center text-muted-foreground">No entries yet. Click “Add” to create one.</p>
          : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((p) => (
                <Card key={p.id} className="border p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg border bg-white">
                      {p.logo ? <img src={p.logo} alt={p.name} className="h-full w-full object-contain" /> : <Building2 className="h-5 w-5 text-muted-foreground" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-heading text-sm font-semibold">{p.name}</h3>
                      <span className="text-xs text-muted-foreground">{p.entity_type || "—"}</span>
                    </div>
                    <Badge className={p.active ? "bg-brand-green" : "bg-muted-foreground"}>{p.active ? "Active" : "Hidden"}</Badge>
                  </div>
                  {p.products && <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{p.products}</p>}
                  {p.website && <a href={p.website.startsWith("http") ? p.website : `https://${p.website}`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"><Globe className="h-3 w-3" />Website</a>}
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Show</span>
                      <Switch checked={p.active} onCheckedChange={() => toggle(p)} />
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setEdit(p)}><Pencil className="h-4 w-4" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete {p.name}?</AlertDialogTitle>
                            <AlertDialogDescription>It will be removed from the site.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(p)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>Edit</DialogTitle>
            <DialogDescription>Update this entry.</DialogDescription></DialogHeader>
          {edit && <PartnerForm initial={edit} kind={kind} onSave={update} />}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
